import React from 'react';
import PropTypes from 'prop-types';
import { Box, Avatar, Typography } from '@mui/material';
import Edit from '@mui/icons-material/Edit';
import CollectionsIcon from '@mui/icons-material/Collections';
import PersonIcon from '@mui/icons-material/Person';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import BusinessIcon from '@mui/icons-material/Business';
import {
  Card,
  LeftButton,
  RightButton,
  FontName,
  IconButtons,
  flexAlignCenterGap,
  flexAlignCenter,
} from '../../../utils/common/styles/commonStyles';

const isActive = (endDate) => {
  if (!endDate) return false;
  const subscriptionEnd = new Date(endDate);
  const today = new Date();
  return subscriptionEnd > today;
};

const CompanyCard = ({ company, onClick, onEdit, onManageUsers, onViewDetails }) => {
  const statusActive = isActive(company.subscriptionEndDate);

  return (
    <Card onClick={onClick}>
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          right: 0,
          paddingX: 1.5,
          paddingY: 0.5,
          borderRadius: '0px 12px 0px 12px',
          background: statusActive
            ? 'linear-gradient(135deg, #10B981 0%, #059669 100%)'
            : 'linear-gradient(135deg, #EF4444 0%, #DC2626 100%)',
          boxShadow: statusActive
            ? '0 6px 16px rgba(16, 185, 129, 0.25), 0 2px 8px rgba(0, 0, 0, 0.15)'
            : '0 6px 16px rgba(239, 68, 68, 0.25), 0 2px 8px rgba(0, 0, 0, 0.15)',
          fontSize: '0.7rem',
          fontWeight: 700,
          color: 'white',
          letterSpacing: '0.5px',
          ...flexAlignCenter,
          justifyContent: 'center',
          minWidth: '70px',
          transition: 'all 0.3s ease',
          '&:hover': {
            boxShadow: statusActive
              ? '0 4px 12px rgba(16, 185, 129, 0.2), 0 2px 6px rgba(0, 0, 0, 0.1)'
              : '0 4px 12px rgba(239, 68, 68, 0.2), 0 2px 6px rgba(0, 0, 0, 0.1)',
            transform: 'scale(1.05)',
          },
        }}
      >
        {statusActive ? 'Active' : 'Inactive'}
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box sx={{ ...flexAlignCenterGap(1.5) }}>
          <Avatar
            sx={{
              width: 40,
              height: 40,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              boxShadow: '0 4px 14px rgba(102, 126, 234, 0.25)',
              border: '2px solid rgba(255, 255, 255, 0.8)',
            }}
          >
            <BusinessIcon sx={{ color: 'white', fontSize: '1.2rem' }} />
          </Avatar>
          <FontName component="span">{company.companyName}</FontName>
        </Box>

        <IconButtons
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            onEdit();
          }}
        >
          <Edit fontSize="small" />
        </IconButtons>
      </Box>

      <Box sx={{ mt: 2.5, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <Box sx={{ ...flexAlignCenterGap(1) }}>
          <CollectionsIcon
            sx={{
              color: '#10B981',
              fontSize: '1.1rem',
              filter: 'drop-shadow(0 2px 4px rgba(16, 185, 129, 0.2))',
            }}
          />
          <Typography
            variant="body2"
            sx={{ color: '#666666', fontSize: '0.9rem', fontWeight: 500 }}
          >
            Collections:
          </Typography>
          <Typography
            component="span"
            sx={{ fontWeight: 700, color: '#000000', fontSize: '0.95rem', padding: '2px 8px' }}
          >
            {company.collectionCount ?? 0}
          </Typography>
        </Box>

        <Box sx={{ ...flexAlignCenterGap(1) }}>
          <PersonIcon
            sx={{
              color: '#F59E0B',
              fontSize: '1.1rem',
              filter: 'drop-shadow(0 2px 4px rgba(245, 158, 11, 0.2))',
            }}
          />
          <Typography
            variant="body2"
            sx={{ color: '#666666', fontSize: '0.9rem', fontWeight: 500 }}
          >
            Created by:
          </Typography>
          <Typography
            component="span"
            sx={{ fontWeight: 600, color: '#000000', fontSize: '0.95rem', padding: '2px 8px' }}
          >
            {company.createdByUser?.name ?? '—'}
          </Typography>
        </Box>

        <Box sx={{ ...flexAlignCenterGap(1) }}>
          <CalendarTodayIcon
            sx={{
              color: '#8B5CF6',
              fontSize: '1.1rem',
              filter: 'drop-shadow(0 2px 4px rgba(139, 92, 246, 0.2))',
            }}
          />
          <Typography
            variant="body2"
            sx={{ color: '#666666', fontSize: '0.9rem', fontWeight: 500 }}
          >
            Subscription:
          </Typography>
          <Typography
            component="span"
            sx={{ fontWeight: 600, color: '#000000', fontSize: '0.95rem', padding: '2px 8px' }}
          >
            {company.subscriptionEndDate
              ? new Date(company.subscriptionEndDate).toLocaleDateString()
              : '—'}
          </Typography>
        </Box>
      </Box>

      <Box sx={{ ...flexAlignCenterGap(1.5), mt: 3 }}>
        <LeftButton
          variant="outlined"
          onClick={(e) => {
            e.stopPropagation();
            onManageUsers();
          }}
          id={`manage-users-${company.id}`}
        >
          Manage Users
        </LeftButton>

        <RightButton
          variant="contained"
          onClick={(e) => {
            e.stopPropagation();
            onViewDetails();
          }}
        >
          View Details
        </RightButton>
      </Box>
    </Card>
  );
};

CompanyCard.propTypes = {
  company: PropTypes.object.isRequired,
  onClick: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  onManageUsers: PropTypes.func.isRequired,
  onViewDetails: PropTypes.func.isRequired,
};

export default CompanyCard;
